// src/config/businessAreasConfig.ts
import { Coins } from "lucide-react";
import {
    Building2,
    Map,
    Building,
    Wrench,
    Briefcase,
    CircleDollarSign,
    Users,
    Code,
    Scale,
    ShoppingCart,
    Leaf,
    PiggyBank,
    LayoutGrid,
    Factory
  } from "lucide-react";
  
  // Typer för konfigurations-objektet
  export interface BusinessAreaConfig {
    name: string;
    icon: React.ComponentType<any>;
    description: string;
    colorProfile: ColorProfile;
    fallback?: boolean; // Om detta är en fallback-ikon för okända områden
    aliases?: string[]; // Alternativa namn för samma affärsområde
  }
  
  export interface ColorProfile {
    gradient: string;
    text: string;
    hoverGradient?: string;
  }
  
  // Fördefinierade färgprofiler
  export const colorProfiles: Record<string, ColorProfile> = {
    orange: {
      gradient: "bg-gradient-to-br from-orange-500 to-amber-400",
      text: "text-white"
    },
    blue: {
      gradient: "bg-gradient-to-br from-blue-600 to-blue-400",
      text: "text-white"
    },
    green: {
      gradient: "bg-gradient-to-br from-emerald-500 to-teal-400",
      text: "text-white"
    },
    purple: {
      gradient: "bg-gradient-to-br from-violet-600 to-purple-400",
      text: "text-white"
    },
    pink: {
      gradient: "bg-gradient-to-br from-rose-500 to-pink-400",
      text: "text-white"
    },
    teal: {
      gradient: "bg-gradient-to-br from-teal-500 to-cyan-400",
      text: "text-white"
    },
    indigo: {
      gradient: "bg-gradient-to-br from-indigo-600 to-blue-500",
      text: "text-white"
    },
    amber: {
      gradient: "bg-gradient-to-br from-amber-500 to-yellow-400",
      text: "text-white"
    },
    red: {
      gradient: "bg-gradient-to-br from-red-600 to-orange-400",
      text: "text-white"
    },
    lime: {
      gradient: "bg-gradient-to-br from-lime-500 to-green-400",
      text: "text-white"
    }
  };
  
  // Konfigurations-objekt för affärsområden
  export const businessAreasConfig: BusinessAreaConfig[] = [
    {
      name: "Fastighet",
      icon: Building2,
      description: "Installation av solanläggning på tak",
      colorProfile: colorProfiles.orange
    },
    {
      name: "Development",
      icon: Factory,
      description: "Anskaffning av mark",
      colorProfile: colorProfiles.green,
      aliases: ["Mark"] // Allowed alternative names
    },
    {
      name: "Park",
      icon: Leaf, // Använder Leaf som är tillgänglig i lucide-react
      description: "Installation av solpark",
      colorProfile: colorProfiles.lime
    },
    {
      name: "Service",
      icon: Wrench,
      description: "Service & underhåll av uppförda anläggningar",
      colorProfile: colorProfiles.blue
    },
    {
      name: "Stab",
      icon: Briefcase,
      description: "Administration och ledning",
      colorProfile: colorProfiles.purple
    },
    {
      name: "Ekonomi",
      icon: PiggyBank,
      description: "Löpande ekonomiuppföljning",
      colorProfile: colorProfiles.teal
    },
    {
      name: "IT",
      icon: Code,
      description: "IT och systemstöd",
      colorProfile: colorProfiles.indigo
    },
    {
      name: "Legal",
      icon: Scale,
      description: "Lag och rätt",
      colorProfile: colorProfiles.red
    },
    {
      name: "Light Leap",
      icon: ShoppingCart,
      description: "Sortiment och E-handel",
      colorProfile: colorProfiles.amber
    },
    {
      name: "HR",
      icon: Users,
      description: "HR & Personal",
      colorProfile: colorProfiles.pink
    },
    // Fallback för okända områden - kommer användas om inget annat matchar
    {
      name: "default",
      icon: Building, // Enklare byggnad som fallback
      description: "Processinformation",
      colorProfile: colorProfiles.purple,
      fallback: true
    }
  ];

  /**
   * Kontrollerar om ett givet namn är ett alias för ett affärsområde och returnerar rätt områdesnamn
   */
  export const resolveBusinessAreaName = (areaName: string): string => {
    // Exakt matchning först
    const exactMatch = businessAreasConfig.find(
      config => config.name.toLowerCase() === areaName.toLowerCase()
    );
    
    if (exactMatch) {
      return exactMatch.name;
    }
    
    // Kolla om det är ett alias
    const aliasMatch = businessAreasConfig.find(
      config => config.aliases?.some(alias => 
        alias.toLowerCase() === areaName.toLowerCase()
      )
    );
    
    if (aliasMatch) {
      return aliasMatch.name;
    }
    
    // Om ingen matchning, returnera ursprungliga namnet
    return areaName;
  };
  
  /**
   * Matchar ett affärsområde med konfiguration
   */
  export const getBusinessAreaConfig = (areaName: string): BusinessAreaConfig => {
    // Normalisera affärsområdesnamnet
    const normalizedName = resolveBusinessAreaName(areaName);
    
    // Exakt matchning först
    const exactMatch = businessAreasConfig.find(
      config => config.name.toLowerCase() === normalizedName.toLowerCase()
    );
    
    if (exactMatch) {
      return exactMatch;
    }
    
    // Delvis matchning - kolla om områdesnamnet innehåller ett konfignamn
    const partialMatch = businessAreasConfig.find(
      config => !config.fallback && normalizedName.toLowerCase().includes(config.name.toLowerCase())
    );
    
    if (partialMatch) {
      return partialMatch;
    }
    
    // Fallback till default
    return businessAreasConfig.find(config => config.fallback) || businessAreasConfig[0];
  };
  
  /**
   * Hämtar alla tillgängliga färgprofiler
   */
  export const getAvailableColorProfiles = (): Record<string, ColorProfile> => {
    return colorProfiles;
  };
  
  /**
   * Returnerar en slumpmässig färgprofil
   */
  export const getRandomColorProfile = (): ColorProfile => {
    const keys = Object.keys(colorProfiles);
    const randomKey = keys[Math.floor(Math.random() * keys.length)];
    return colorProfiles[randomKey];
  };
